import type { Extensions } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import DropCursor from '@tiptap/extension-dropcursor';
import GapCursor from '@tiptap/extension-gapcursor';
import Heading, { type Level } from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';

import { ImageExtension, LatexNode, TableCellExtension, TableHeaderExtension } from './extensions';
import type { AvailableControls, ControlsInput, ImageUploadFunction } from './types';

type TiptapExtension = Extensions[number];
type ControlType = Exclude<AvailableControls, 'clearFormatting'>;

export function getExtensions({
  onImageUpload,
  placeholder,
  controls,
}: {
  onImageUpload?: ImageUploadFunction;
  placeholder?: string;
  controls?: ControlsInput;
}) {
  const baseExtensions: Extensions = [
    Document,
    Text,
    Paragraph,
    DropCursor,
    GapCursor,
    Placeholder.configure({ placeholder }),
  ];

  if (!controls?.length) {
    return baseExtensions;
  }

  const uniqueControls = new Set(controls.flat()) as Set<ControlType>;

  const sharedExtensions = new Map<string, TiptapExtension>();

  // Handle heading controls
  const headingLevels = ['h1', 'h2', 'h3']
    .filter((headingLevel) => uniqueControls.has(headingLevel as ControlType))
    .map((headingLevel) => parseInt(headingLevel.slice(1))) as Level[];
  if (headingLevels.length) {
    sharedExtensions.set('heading', Heading.configure({ levels: headingLevels }));
  }

  // Handle alignment controls
  const alignmentTypes = ['alignLeft', 'alignCenter', 'alignRight']
    .filter((alignmentType) => uniqueControls.has(alignmentType as ControlType))
    .map((alignmentType) => alignmentType.replace('align', '').toLowerCase());
  if (alignmentTypes.length) {
    sharedExtensions.set(
      'textAlign',
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: alignmentTypes }),
    );
  }

  // Static extension mappings
  const extensionMap: Record<string, () => TiptapExtension[]> = {
    image: () => [ImageExtension.configure({ onImageUpload })],
    latex: () => [LatexNode],
    beta: () => [LatexNode],
    sd: () => [LatexNode],
    summation: () => [LatexNode],
    table: () => [
      TableCellExtension,
      TableHeaderExtension,
      Table.configure({ resizable: true }),
      TableRow,
    ],
    bold: () => [Bold],
    italic: () => [Italic],
    underline: () => [Underline],
    strikethrough: () => [Strike],
    subscript: () => [Subscript],
    superscript: () => [Superscript],
    undo: () => [History],
    redo: () => [History],
    bulletList: () => [ListItem, BulletList],
    numberList: () => [ListItem, OrderedList],
  } as const;

  // Get additional extensions
  const additionalExtensions = Array.from(uniqueControls)
    .filter((control) => control in extensionMap)
    .flatMap((control) => extensionMap[control as keyof typeof extensionMap]());

  return [
    ...new Set([...baseExtensions, ...sharedExtensions.values(), ...additionalExtensions]),
  ].filter(Boolean) as Extensions;
}
